<template>
  <div class="card">
    <div class="card-body">
      <p>
        <small class="font-weight-bold">{{ $t('dashboard.dhm-performance') }}</small>
      </p>
      <highcharts :options="chartOptions" />
    </div>
  </div>
</template>

<script>
import { format, sub } from 'date-fns'
import enLocale from 'date-fns/locale/en-US'
import idLocale from 'date-fns/locale/id'
import regression from 'regression'
import api from '@/utils/Api'

const COLORS = {
  all: '#A04D00',
  trend: '#006CA0',
  low: '#00F60A',
  medium: '#F7D214',
  high: '#FF0000'
}

const DATE_FORMATS = {
  daily: 'dd MMM',
  monthly: 'MMM'
}

const LOCALES = {
  en: enLocale,
  id: idLocale
}

export default {
  props: {
    filters: {
      type: Object,
      default: () => ({
        period: 'daily',
        startDate: sub(new Date(), { days: 30 }),
        endDate: new Date(),
        risk: []
      })
    },
    isApply: {
      type: Boolean,
      default: () => (false)
    }
  },

  data: () => ({
    chartOptions: {
      chart: {
        type: 'spline'
      },
      title: false,
      credits: false,
      xAxis: {
        categories: [],
        labels: {
          formatter: function () {
            return this.value
          }
        }
      },
      yAxis: {
        title: false
      },
      legend: {
        layout: 'vertical',
        align: 'right',
        verticalAlign: 'middle'
      },
      responsive: {
        rules: [{
          condition: {
            maxWidth: 720
          },
          chartOptions: {
            legend: {
              layout: 'horizontal',
              align: 'center',
              verticalAlign: 'bottom'
            }
          }
        }]
      },
      series: []
    }
  }),

  computed: {
    builtFilters () {
      return [
        `period=${this.filters.period}`,
        `startDate=${format(this.filters.startDate, 'yyyy-MM-dd')}`,
        `endDate=${format(this.filters.endDate, 'yyyy-MM-dd')}`,
        ...this.filters.organization.map(organization => `organization=${organization}`),
        ...this.filters.workLocation.map(workLocation => `workLocation=${workLocation}`),
        ...this.filters.employee.map(employee => `employeeType=${employee}`),
        ...this.filters.risk.map(risk => `risk=${risk}`)
      ].join('&')
    }
  },

  watch: {
    '$i18n.locale': 'localize',
    isApply: {
      deep: true,
      immediate: true,
      handler: 'fetch'
    }
  },

  created () {
    this.localize()
  },

  methods: {
    async fetch () {
      const { data } = await api.dashboard.lineChart(this.builtFilters)
      this.chartOptions.xAxis.categories = Object.keys(data)
      this.chartOptions.series = this.filters.risk.length
        ? this.getRiskSeries(data, this.filters.risk)
        : this.getTotalSeries(data)
    },
    getRiskSeries: (data, risks) => risks.map(risk => ({
      name: `${risk.charAt(0).toUpperCase()}${risk.substr(1)} Risk`,
      color: COLORS[risk],
      showInLegend: true,
      data: Object.values(data).map(date => date[risk]),
      dataLabels: {
        enabled: true
      }
    })),
    getTotalSeries: data => ([
      {
        name: 'All Risk Level',
        color: COLORS.all,
        showInLegend: true,
        data: Object.values(data).map(item => item.Total),
        dataLabels: {
          enabled: true
        }
      },
      {
        name: 'Trend',
        color: COLORS.trend,
        showInLegend: false,
        marker: false,
        data: regression.polynomial(
          Object.values(data).map((item, idx) => ([idx + 1, item.Total])),
          {
            order: 5,
            precision: 20
          }
        ).points.map(item => item[1]),
        dataLabels: {
          enabled: false
        }
      }
    ]),
    localize () {
      const thisComponent = this
      this.chartOptions.xAxis.labels.formatter = function () {
        return format(
          new Date(this.value),
          DATE_FORMATS[thisComponent.filters.period],
          { locale: LOCALES[thisComponent.$i18n.locale] }
        )
      }
    }
  }
}
</script>
